import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { debounce } from "lodash";
import { ADMIN_ROLES } from "../../utils/constants";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Input,
  InputAdornment,
  Switch,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { EditOutlined } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Helmet } from "react-helmet-async";
import { sportNewsApi, sportNewsCategoryApi } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { Loader } from "../../components/CustomLoader";
import TableFilter from "../../components/shared/TableFilter";
import { STATUS_OPTIONS_LIST, IMAGE_BASE } from "../../utils/constants";
import { fDate } from "../../utils/formatTime";
import { capitalize, TrimText } from "../../utils/helpers";

export default function SportNewsList() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const isReadOnly = user?.role === ADMIN_ROLES.MGT || user?.role === ADMIN_ROLES.JUNIOR;
  const [searchItem, setSearchItem] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [warning, setWarning] = useState("");
  const [show, setShow] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [status, setStatus] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [categoryMap, setCategoryMap] = useState({});

  useEffect(() => {
    sportNewsCategoryApi
      .getAllSportNewsCategories({ pageNumber: 1, pageSize: 200 })
      .then((res) => {
        const map = {};
        (res?.data?.data?.result || []).forEach((cat) => {
          map[cat._id] = cat.name;
        });
        setCategoryMap(map);
      })
      .catch((err) => {
        errorHandler(err);
      });
  }, []);

  const fetchSportNews = async (query = searchItem, page = currentPage, size = pageSize, selectedStatus = status) => {
    try {
      setIsLoading(true);
      setLoaderDelay(false);
      const body = {
        searchItem: query,
        pageNumber: page,
        pageSize: size,
        status: selectedStatus,
      };

      const res = await sportNewsApi.getAllSportNews(body);
      if (res.status === 200) {
        setTimeout(() => {
          setData(res?.data?.data?.result || []);
          setTotalPages(res?.data?.data?.totalRecords || 0);
          setLoaderDelay(true);
        }, 500);
      }
    } catch (err) {
      errorHandler(err);
      setLoaderDelay(true);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => {
      setCurrentPage(1);
      fetchSportNews(query, 1, pageSize, status);
    }, 500),
    [pageSize, status]
  );

  const handleSearchChange = (e) => {
    setSearchItem(e.target.value);
    debouncedSearch(e.target.value);
  };

  useEffect(() => {
    fetchSportNews(searchItem, currentPage, pageSize, status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, status, refresh]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const columns = [
    {
      name: "S.No.",
      sortable: false,
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "75px"
    },
    {
      name: "Image",
      cell: (row) => (
        <img 
          src={row.image ? `${IMAGE_BASE}${row.image.replace(/\\/g, '/')}` : "/assets/placeholder.svg"} 
          alt="news" 
          style={{ width: "50px", height: "50px", objectFit: "cover", borderRadius: "5px", margin: "5px 0" }} 
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "/assets/placeholder.svg";
          }}
        />
      ),
      width: "100px"
    },
    {
      name: "Title",
      selector: (row) => TrimText(row?.title || "", 30),
      sortable: true,
      width: "250px"
    },
    {
      name: "Category",
      selector: (row) =>
        capitalize(categoryMap[row?.categoryId] || "N/A"),
      sortable: true,
    },
    {
      name: "Author",
      selector: (row) => capitalize(row?.author || ""),
      sortable: true,
    },
    {
      name: "Trending",
      cell: (row) => (
        <span style={{ color: row.featured ? "green" : "gray", fontWeight: row.featured ? "bold" : "normal" }}>
          {row.featured ? "Yes" : "No"}
        </span>
      ),
      sortable: true,
      width: "100px"
    },
    {
      name: "Created At",
      sortable: true,
      selector: (row) => capitalize(fDate(row.createdAt)),
      width: "150px"
    },
    ...(!isReadOnly ? [{
      name: "Status",
      width: "100px",
      cell: (row) => (
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={row.status === 1}
                onClick={() => {
                  setShow(true);
                  setCurrentStatus(row.status);
                  setStatusId(row._id);
                  setWarning(
                    `Are you sure you want to ${row.status === 1 ? "deactivate" : "activate"} this news entry?`
                  );
                }}
                inputProps={{ "aria-label": "controlled" }}
              />
            }
          />
        </FormGroup>
      ),
    }] : []),
    {
      name: "Action",
      right: true,
      cell: (row) => (
        <>
          <Tooltip title="View" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/sport-news/view/${row?._id}`);
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          {!isReadOnly && (
            <>
              <Tooltip title="Edit" placement="top">
                <IconButton
                  color="primary"
                  onClick={() => {
                    navigate(`/sport-news/edit/${row?._id}`);
                  }}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete" placement="top">
                <IconButton color="primary" onClick={() => deleteData(row?._id)}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </>
      ),
    },
  ];

  const deleteData = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await sportNewsApi.deleteSportNews(id);
          if (res?.status === 200) {
            toast.success(res?.data?.message || "Deleted successfully", { toastId: "success" });
            setRefresh(!refresh);
          } else {
            toast.error(res?.data?.message || "Failed to delete", { toastId: "error" });
          }
        } catch (error) {
          console.error("Error deleting...", error);
          errorHandler(error);
        }
      }
    });
  };

  const toggleChecked = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;

      const res = await sportNewsApi.changeStatus({ status: newStatus }, id);
      setCurrentStatus("");

      if (res.status === 200 && res?.data?.message) {
        toast.success(res?.data?.message, { toastId: "success1" });
        setRefresh(!refresh);
        setShow(false);
      } else {
        toast.error(res?.data?.message || "Failed to update status", { toastId: "error1" });
        setShow(false);
      }
    } catch (err) {
      setShow(false);
      errorHandler(err);
    }
  };

  return (
    <Card>
      <Helmet>
        <title>Sports News Management</title>
      </Helmet>
      <CardHeader titleTypographyProps={{ variant: "h4" }} title="Sports News Management" />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <TableFilter
          data={STATUS_OPTIONS_LIST}
          value={status}
          setValue={(newVal) => {
            setCurrentPage(1);
            setStatus(newVal);
          }}
          filterName="Status"
        />
        <Input
          label="Search"
          variant="standard"
          value={searchItem}
          placeholder="Search by Title..."
          onChange={handleSearchChange}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="clear-search"
                onClick={() => {
                  setSearchItem("");
                  debouncedSearch("");
                }}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />
        {!isReadOnly && (
          <Button
            size="large"
            type="submit"
            variant="contained"
            sx={{ ml: 3, mr: 3 }}
            onClick={() => navigate("/sport-news/add")}
          >
            Add
          </Button>
        )}
      </Box>
      <CardContent>
        <DataTable
          customStyles={{
            headCells: {
              style: {
                fontSize: "15px",
                fontWeight: "bold",
              },
            },
          }}
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalPages}
          onChangePage={handlePageChange}
          paginationPerPage={pageSize}
          paginationRowsPerPageOptions={[10, 20, 50]}
          onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
            setPageSize(currentRowsPerPage);
            setCurrentPage(currentPage);
          }}
          progressPending={isLoading || !loaderDelay}
          progressComponent={<Loader />}
          className="custom"
        />
      </CardContent>

      <Dialog open={show} onClose={() => setShow(false)} className="popupbox-statis">
        <DialogTitle>{warning}</DialogTitle>
        <DialogActions className="status-box-action">
          <Button
            variant="outlined"
            onClick={() => {
              toggleChecked(statusId, currentStatus);
              setShow(false);
            }}
            color="primary"
          >
            Yes
          </Button>
          <Button variant="outlined" onClick={() => setShow(false)} color="primary">
            No
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
